import type { AxiosInstance } from 'axios';

// Dedicated Report APIs aligning with web Frontend ReportsContext.jsx
// Core functions: raw HTTP only

const getEmployeesReportCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  startISO: string,
  endISO: string,
  auth: { accessToken?: string | null },
  employeeId?: string,
) => {
  const employeeQuery = employeeId
    ? `&bp_employee_id=${encodeURIComponent(employeeId)}`
    : '';
  return axiosinstance.get(
    `/bp_get_employees_reports_by_date_information?page=${page}&limit=${limit}&bp_start_date=${encodeURIComponent(
      startISO,
    )}&bp_end_date=${encodeURIComponent(endISO)}${employeeQuery}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const getEmployeeReportCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  employeeId: string,
  page: number,
  limit: number,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(
    `/bp_get_employee_reports_information/${employeeId}?page=${page}&limit=${limit}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const getPaymentReportCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  startISO: string,
  endISO: string,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(
    `/bp_get_payment_reports_information?page=${page}&limit=${limit}&bp_start_date=${encodeURIComponent(
      startISO,
    )}&bp_end_date=${encodeURIComponent(endISO)}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const getOpeningAndClosingStockReportCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  startISO: string,
  endISO: string,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(
    `/bp_get_opening_and_closing_stock_report_information?page=${page}&limit=${limit}&bp_start_date=${encodeURIComponent(
      startISO,
    )}&bp_end_date=${encodeURIComponent(endISO)}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const getAllSalesReportCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(
    `/bp_get_all_sales_report_information?page=${page}&limit=${limit}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const getAllSalesReportWithFilterCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  startISO: string,
  endISO: string,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(
    `/bp_get_all_sales_report_information?page=${page}&limit=${limit}&bp_start_date=${encodeURIComponent(
      startISO,
    )}&bp_end_date=${encodeURIComponent(endISO)}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const getDailySalesReportCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  month: number | string,
  year: number | string,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(
    `/bp_get_sales_made_monthly_by_dates?page=${page}&limit=${limit}&bp_month=${encodeURIComponent(
      String(month),
    )}&bp_year=${encodeURIComponent(String(year))}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const getAllProductsMonthlySalesReportCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  year: number | string,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(
    `/bp_get_all_products_monthly_sales_report_information?bp_year=${encodeURIComponent(
      String(year),
    )}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const getMonthlySalesTargetCore = async (
  axiosinstance: AxiosInstance,
  month: number | string,
  year: number | string,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(
    `/bp_get_monthly_sales_target_information?bp_sales_target_month=${encodeURIComponent(
      String(month),
    )}&bp_sales_target_year=${encodeURIComponent(String(year))}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

// Create monthly sales target
const addMonthlySalesTargetCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  month: number | string,
  year: number | string,
  amount: number,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.post(
    `/bp_add_monthly_sales_target_information`,
    JSON.stringify({
      bp_sales_target_month: String(month),
      bp_sales_target_year: String(year),
      bp_sales_target: amount,
    }),
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

// Update monthly sales target
const updateMonthlySalesTargetCore = async (
  axiosinstance: AxiosInstance,
  month: number | string,
  year: number | string,
  amount: number,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.put(
    `/bp_update_monthly_sales_target_information`,
    {
      bp_sales_target_month: String(month),
      bp_sales_target_year: String(year),
      bp_sales_target_amount: amount,
    },
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

// Public wrappers with basic error propagation
export const getEmployeesReport = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  startISO: string,
  endISO: string,
  auth: { accessToken?: string | null },
  /** Limits the report to one employee (servers that support it echo `bp_employee_id`). */
  employeeId?: string,
) => {
  try {
    return await getEmployeesReportCore(
      axiosinstance,
      ownerId,
      page,
      limit,
      startISO,
      endISO,
      auth,
      employeeId,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getEmployeeReport = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  employeeId: string,
  page: number,
  limit: number,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getEmployeeReportCore(
      axiosinstance,
      ownerId,
      employeeId,
      page,
      limit,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getPaymentReport = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  startISO: string,
  endISO: string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getPaymentReportCore(
      axiosinstance,
      ownerId,
      page,
      limit,
      startISO,
      endISO,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getOpeningAndClosingStockReport = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  startISO: string,
  endISO: string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getOpeningAndClosingStockReportCore(
      axiosinstance,
      ownerId,
      page,
      limit,
      startISO,
      endISO,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getAllSalesReport = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getAllSalesReportCore(
      axiosinstance,
      ownerId,
      page,
      limit,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getAllSalesReportWithFilter = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  startISO: string,
  endISO: string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getAllSalesReportWithFilterCore(
      axiosinstance,
      ownerId,
      page,
      limit,
      startISO,
      endISO,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getDailySalesReport = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  month: number | string,
  year: number | string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getDailySalesReportCore(
      axiosinstance,
      ownerId,
      page,
      limit,
      month,
      year,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getAllProductsMonthlySalesReport = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  year: number | string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getAllProductsMonthlySalesReportCore(
      axiosinstance,
      ownerId,
      year,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getMonthlySalesTarget = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  month: number | string,
  year: number | string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getMonthlySalesTargetCore(axiosinstance, month, year, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const addMonthlySalesTarget = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  month: number | string,
  year: number | string,
  amount: number,
  auth: { accessToken?: string | null },
) => {
  try {
    return await addMonthlySalesTargetCore(
      axiosinstance,
      ownerId,
      month,
      year,
      amount,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const updateMonthlySalesTarget = async (
  axiosinstance: AxiosInstance,
  month: number | string,
  year: number | string,
  amount: number,
  auth: { accessToken?: string | null },
) => {
  try {
    return await updateMonthlySalesTargetCore(
      axiosinstance,
      month,
      year,
      amount,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};
